import React, { Fragment } from 'react';
import Header from '@components/Header';
import Page from '@components/Page';
import { graphql } from 'gatsby';
import GatsbyImage from 'gatsby-image';
import { css } from '@emotion/react';
import Footer from '@components/Footer';
import { has } from 'lodash';
import BackgroundImage from 'gatsby-background-image';
import FrontpageLink from '@components/FrontpageLink';
import ResponsiveVideo from '@components/ui/ResponsiveVideo';
import PageTitle from '@components/ui/PageTitle';
import CtaButton from '@components/ui/CtaButton';
import TestimonialBlock from '@components/ui/TestimonialBlock';
import Hero from '@theme/components/Hero';
import Section from '@theme/components/Section';

const utmMedium = 'videos';

export default function FeaturesTemplate({
  data: {
    FeaturesHeroBG,
    greenBackground,
    testimonialsPage: {
      testimonials
    },
    videosPage: {
      videos
    },
    featuresPage: {
      /*acf: { language },*/
      features: {
        features_title: title,
        features_subtitle: subtitle,
        features_section_title: sectionTitle,
        features_benefits: benefits,
        features_list: list,
        features_todo_title: todoTitle,
        features_todo_steps: todoSteps,
        link
      }
    }
  },
  pageContext: { language, refs, layouts }
}) {
  return (
    <Page>
      <Hero title={title}>
        <Header
          language={language}
          refs={refs}
          layouts={layouts}
        />
      </Hero>
      <Section>
        <div css={css`
          padding: 4rem 0;
        `}
        >
          <h1 className="text-center pb-2 mb-5">
            {subtitle}
          </h1>
          <div className="container">
            <div className="row">
              {benefits.map(({ entry }, i) => {
                return (
                  <div className="col" key={i}>
                    <div className="text-center">
                      {has(entry, 'entry_image.localFile.sharp.fixed') && (
                        <GatsbyImage
                          fixed={entry.entry_image.localFile.sharp.fixed}
                        >
                        </GatsbyImage>
                      )}
                    </div>
                    <div className="text-center">
                      <h2>{entry.entry_title}</h2>
                    </div>
                  </div>
                );
              })}
            </div>
            <CtaButton link={link} utmMedium={utmMedium} utmContent="cta_button_benefits" />
          </div>
        </div>
      </Section>
      <Section
        className='bg-primary'
        dividerTop={{ variant: 3 }}
        dividerBottom={{ variant: 1 }}
      >
        <div className="container">
          <h1 className="text-center text-white text-uppercase font-weight-light pb-2 mb-5">
            {sectionTitle}
          </h1>
          <div className="row">
            {list.map(({ entry }, i) => {
              return (
                <div className="col-md-4 mb-5" key={i}>
                  <div className="card card-body" css={css`height: 100%;`}
                  >
                    <div className="text-center">
                      {has(entry, 'entry_image.localFile.sharp.fixed') && (
                        <GatsbyImage
                          fixed={entry.entry_image.localFile.sharp.fixed}
                        />
                      )}
                    </div>
                    <div className="text-center">
                      <h4>{entry.entry_title}</h4>
                      <div dangerouslySetInnerHTML={{ __html: entry.entry_description }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <CtaButton link={link} utmMedium={utmMedium} utmContent="cta_button_features" dark />
        </div>
      </Section>
      <Section>
        <h1 className="text-center mt-5">{videos.title}</h1>
        <div className="container py-5">
          <div className="row">
            {videos.videos.map(({ title, video_id: videoId, description }, i) =>
              <div className="col" key={i}>
                <ResponsiveVideo title={title} videoId={videoId} />
                <h2 className="text-center mt-3">{description}</h2>
              </div>
            )}
          </div>
          <CtaButton
            link={videos.link}
            utmMedium={utmMedium}
            utmContent="cta_button_videos"
          />
        </div>
      </Section>
      <Section
        className='bg-primary-alt'
        dividerTop={{ variant: 3 }}
        dividerBottom={{ variant: 4 }}
      >
        <h1 className="text-center mt-5">{testimonials.title}</h1>
        <div className='container py-5'>
          {testimonials.testimonials.map((testimonial, index) =>
            <TestimonialBlock key={index} testimonial={testimonial} />
          )}
        </div>
      </Section>
      <Section>
        <h1 className="text-center mt-5">{todoTitle}</h1>
        <div className='container py-5'>
          <div>
            {todoSteps.map(({ features_todo_step: step }, index) =>
              <div className="d-flex mb-4" key={index}>
                <div className="process-circle bg-primary" />
                <div className="d-flex align-items-center ml-3">
                  <h5 className="mb-0">{`${index+1}. ${step}`}</h5>
                </div>
              </div>
            )}
          </div>
          <CtaButton
            link={link}
            utmMedium={utmMedium}
            utmContent="cta_button_steps"
          />
        </div>
      </Section>
      <Footer
        language={language}
        layouts={layouts}
      />
    </Page>
  );
}

export const query = graphql`
  query($id: Int, $featuresId: Int, $videosId: Int, $testimonialsId: Int) {
    page: wordpressWpWebsites(wordpress_id: { eq: $id }) {
      ...Page
    }
    FeaturesHeroBG: file(relativePath: { eq: "features-hero-bg.png" }) {
     sharp: childImageSharp {
       fluid {
         ...GatsbyImageSharpFluid
        }
      }
    }
    greenBackground: file(relativePath: { eq: "pricing-bg.png" }) {
      sharp: childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    testimonialsPage: wordpressWpCustompages(wordpress_id: { eq: $testimonialsId }) {
      testimonials: childWordPressAcfTestimonialsSection {
        title
        link {
          link_url
          link_text
        }
        testimonials {
          name
          author_name
          author_profession
          text
          image {
            localFile {
              sharp: childImageSharp {
                fixed(width: 160, height: 160) {
                  ...GatsbyImageSharpFixed
                }
              }
            }
          }
          author_image {
            localFile {
              sharp: childImageSharp {
                fixed(width: 160, height: 160) {
                  ...GatsbyImageSharpFixed
                }
              }
            }
          }
        }
      }
    }
    videosPage: wordpressWpCustompages(wordpress_id: { eq: $videosId }) {
      videos: childWordPressAcfVideosSection {
        title
        link {
          link_text
          link_url
          link_style
        }
        videos {
          title
          video_id
          description
          type
        }
      }
    }
    featuresPage: wordpressWpCustompages(wordpress_id: { eq: $featuresId }) {
      acf {
        language
      }
      features: childWordPressAcfFeaturesSection {
        features_title
        features_subtitle
        features_section_title
        features_todo_title
        features_todo_steps {
          features_todo_step
        }
        features_benefits {
          entry {
            entry_description
            entry_style
            entry_title
            entry_image {
              localFile {
                sharp: childImageSharp {
                  fixed(width: 160, height: 160) {
                    ...GatsbyImageSharpFixed
                  }
                }
              }
            }
          }
        }
        features_list {
          entry {
            entry_description
            entry_style
            entry_title
            entry_image {
              localFile {
                sharp: childImageSharp {
                  fixed(width: 140, height: 140) {
                    ...GatsbyImageSharpFixed
                  }
                }
              }
            }
          }
        }
        link {
          link_style
          link_text
          link_url
        }
      }
    }
  }
`;